import { HttpClient, HttpEventType } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { TokenService } from 'src/app/services/token/token.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-audio-recorder',
  templateUrl: './audio-recorder.component.html',
  styleUrls: ['./audio-recorder.component.scss']
})
export class AudioRecorderComponent {
  @Input() vehicleId: string;
  @Input() phase: string;
  @Input() parentPartId: string;
  @Output() onUploadSuccess: EventEmitter<string> = new EventEmitter<string>();
  isRecording = false;
  isPaused = false;
  audioRecorded = false;
  audioBlob: Blob | null = null;
  audioURL: SafeUrl | null = null;
  mediaRecorder: MediaRecorder | null = null;
  audioChunks: Blob[] = [];
  stream: MediaStream | null = null;
  uploadPercentage: number | null = null;

  static readonly AUDIO_RECORDING_SHOULD_START_FROM = 90;

  // Timer related properties
  recordingTime = AudioRecorderComponent.AUDIO_RECORDING_SHOULD_START_FROM;
  timerInterval: ReturnType<typeof setInterval> | null = null;
  pausedTime = 0;

  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;

  constructor(
    private httpClient: HttpClient,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private tokenService: TokenService
  ) {}

  // Format time to MM:SS
  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  startTimer() {
    // If timer was paused, continue from previous time
    this.timerInterval = setInterval(() => {
      const updatedTimer = this.recordingTime - 1;

      if (updatedTimer === 0) {
        this.stopRecording();
      }
      this.recordingTime = updatedTimer;
      // this.recordingTime--;
    }, 1000);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  async startRecording() {
    try {
      // Stop any existing stream
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
      }

      // Get new audio stream
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(this.stream);
      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        // Ensure we have recorded audio chunks
        if (this.audioChunks.length > 0) {
          this.audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });

          // Create a safe URL for the audio
          const unsafeUrl = URL.createObjectURL(this.audioBlob);
          this.audioURL = this.sanitizer.bypassSecurityTrustUrl(unsafeUrl);

          this.audioRecorded = true;
          this.stopTimer();
        }

        // Stop and clean up the stream
        if (this.stream) {
          this.stream.getTracks().forEach((track) => track.stop());
          this.stream = null;
        }

        this.isRecording = false;
        this.isPaused = false;
      };

      // Reset recording time when starting a new recording
      this.recordingTime = AudioRecorderComponent.AUDIO_RECORDING_SHOULD_START_FROM;
      this.mediaRecorder.start();
      this.isRecording = true;
      this.isPaused = false;
      this.startTimer();
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Microphone access is required for recording.');
    }
  }

  pauseRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
      this.isPaused = true;
      this.stopTimer();
    }
  }

  resumeRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
      this.isPaused = false;
      this.startTimer();
    }
  }

  stopRecording() {
    if (
      this.mediaRecorder &&
      (this.mediaRecorder.state === 'recording' || this.mediaRecorder.state === 'paused')
    ) {
      this.mediaRecorder.stop();
      this.isRecording = false;
      this.audioRecorded = true;
      this.stopTimer();
    }
  }

  deleteRecording() {
    if (this.audioURL) {
      // Revoke the object URL to free up memory
      const url = this.audioURL as string;
      URL.revokeObjectURL(url);
    }
    this.audioURL = null;
    this.audioBlob = null;
    this.audioRecorded = false;
    this.audioChunks = [];
    this.recordingTime = 0;

    // Reset the audio player if it exists
    if (this.audioPlayer) {
      this.audioPlayer.nativeElement.src = '';
    }
  }

  uploadRecording() {
    if (!this.audioBlob) {
      return;
    }

    const formData = new FormData();
    formData.append('audio', this.audioBlob);
    formData.append('vehicle_id', this.vehicleId);
    formData.append('phase', this.phase.toLowerCase());
    formData.append('parent_part_id', this.parentPartId);
    this.uploadPercentage = 0;

    this.httpClient
      .post(`${environment().api}/upload/vehicle-audio-video`, formData, {
        reportProgress: true,
        observe: 'events',
        headers: this.tokenService.GetTokenByHeader()
      })
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.uploadPercentage = Math.round((event.loaded / event.total) * 100);
            console.log('here', this.uploadPercentage);
          } else if (event.type === HttpEventType.Response) {
            this.deleteRecording();
            this.toastr.success('Audio uploaded');
            if (event.body?.['success']) {
              this.uploadPercentage = null;
              this.onUploadSuccess.emit(event.body?.['url']);
            }
          }
        },
        error: (error) => {
          this.uploadPercentage = null;
          console.error('Upload failed', error);
        }
      });
  }

  ngOnDestroy() {
    // Clean up any active media streams or created object URLs
    if (this.audioURL) {
      const url = this.audioURL as string;
      URL.revokeObjectURL(url);
    }

    // Stop stream if it's still active
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }

    this.stopTimer();
  }

  onFileReceived(file: File) {
    this.audioBlob = file;

    const unsafeUrl = URL.createObjectURL(this.audioBlob);
    this.audioURL = this.sanitizer.bypassSecurityTrustUrl(unsafeUrl);

    this.audioRecorded = true;
  }
}
